import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useMountEffect } from "primereact/hooks";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import List from "../../../common/list";
import {
  RouteConstants,
  AccessScopeConstants,
  IntegrationConstants,
} from "../../../../constants";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import {
  integrationListRequest,
  integrationDeleteRequest,
  resetAction,
} from "../../../../store/slices/integrationSlice";

const IntegrationList = ({
  labels,
  integrations,
  requesting,
  action,
  integrationListRequest,
  integrationDeleteRequest,
  resetAction,
  user,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  useMountEffect(() => {
    integrationListRequest({});
  });

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.EnterpriseAccess,
    AccessScopeConstants.CreateScope
  );

  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.EnterpriseAccess,
    AccessScopeConstants.UpdateScope
  );

  const canDelete = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.EnterpriseAccess,
    AccessScopeConstants.DeleteScope
  );

  const handleAddClick = () => {
    navigate(RouteConstants.APP_ROUTE_INTEGRATIONS_ADD);
  };

  const onDeleteAction = (integration) => {
    confirmDialog({
      message: intl.formatMessage(
        { id: "delete_object_message" },
        {
          name: integration.name,
          object: intl.formatMessage({ id: "integration" }),
        }
      ),
      header: intl.formatMessage({ id: "delete_confirmation" }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => {
        integrationDeleteRequest({ id: integration.id });
      },
      reject: () => {},
    });
  };

  const statusBodyTemplate = (data) => {
    const status = data?.isEnabled ? "active" : "inactive";
    return (
      <Message
        severity={data?.isEnabled ? "success" : "error"}
        text={intl.formatMessage({ id: status })}
        icon={""}
      />
    );
  };

  const typeBodyTemplate = (rowData) => {
    const typeLabel =
      IntegrationConstants.INTEGRATION_TYPE_LABELS[rowData.type];
    const subType = rowData.subType;

    return (
      <span>
        {typeLabel}
        {subType ? ` - ${intl.formatMessage({ id: subType })}` : ""}
      </span>
    );
  };

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "integrations" })}
          </div>
          <div className="edexlms-page-actions">
            <Button
              icon="pi pi-plus"
              onClick={handleAddClick}
              size="small"
              label={intl.formatMessage({ id: "add_integration" })}
              type="button"
              disabled={!canAdd}
              tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
              tooltip={
                canAdd ? "" : intl.formatMessage({ id: "no_permission" })
              }
            />
          </div>
        </div>
        <div className="edexlms-page-body">
          <List
            loading={requesting}
            pagination={false}
            data={integrations || []}
            selectionMode=""
            handleRowClick={(data) => {
              if (canUpdate) {
                navigate(
                  RouteConstants.APP_ROUTE_INTEGRATIONS_EDIT.replace(
                    ":id",
                    data.data.id
                  )
                );
              }
            }}
            columns={[
              {
                field: "name",
                header: "name",
                sortable: true,
              },
              {
                field: "type",
                header: "type",
                sortable: false,
                body: typeBodyTemplate,
              },
              {
                field: "isEnabled",
                header: "status",
                sortable: false,
                body: statusBodyTemplate,
              },
              {
                field: "created_at",
                header: "created_at",
                sortable: true,
                body: ListCellRenders.DateTime("createdAt"),
              },
              {
                field: "updated_at",
                header: "updated_at",
                sortable: true,
                body: ListCellRenders.DateTime("updatedAt"),
              },
              {
                field: "actions",
                headerIcon: "pi-cog",
                align: "center",
                body: ListCellRenders.OtherActions([
                  {
                    label: "delete",
                    icon: "pi pi-trash",
                    isAllowed: canDelete,
                    action: (data) => onDeleteAction(data),
                  },
                ]),
              },
            ]}
          />
        </div>
      </div>
      <ConfirmDialog />
    </div>
  );
};

const mapStateToProps = (state) => ({
  integrations: state.integrations.data,
  requesting: state.integrations.requesting,
  action: state.integrations.action,
  user: state.self?.user,
});

const mapDispatchToProps = {
  integrationListRequest,
  integrationDeleteRequest,
  resetAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(IntegrationList);
